/**
 * TerrainController — effect-only child component for the TAWS terrain
 * advisor color-ramp rendering.
 *
 * Extracted from MapView.tsx as Phase 6 of the HI-2' refactor
 * (audit synthesis 2026-05-24). Owns:
 *   - Terrain hillshade + color-relief visibility
 *   - TAWS color-ramp computation and application
 *   - rAF coalescing for sub-frame GPS altitude jitter
 *   - altitudeBucket math (25-ft bucket to skip redundant ramp rebuilds)
 *   - applyTerrainRef registration with the style-rebuild registry
 *
 * Returns null — no DOM output.
 *
 * Background on the three bugs this fixes (from MapView comment):
 *   1. Sub-25 ft slider drags didn't re-run the effect (altitudeBucketFt
 *      rounded to 100 ft). Fixed: dep is raw altitudeSliderFt, bucket
 *      math inside.
 *   2. Basemap setStyle() wipe: addOverlays() re-adds terrain-color with
 *      visibility:'none' and an empty ramp; effect doesn't re-fire.
 *      Fixed: applyTerrainRef stash → called from styledata handler.
 *   3. First-paint: no ramp applied until first slider drag.
 *      Fixed: called from map.on('load') too.
 */

import { useEffect, useRef } from 'react';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { useUiStore } from '../../stores/uiStore';
import { useTerrainAdvisorStore } from '../../stores/terrainAdvisorStore';
import { useGpsStore } from '../../stores/gpsStore';
import { getMapInstance } from '../MapView';
import { registerStyleRebuild } from '../layerVisibilityRegistry';

const CONUS_MAX_TERRAIN_FT = 20_000;
const FT_PER_M = 3.28084;

export function TerrainController() {
  const showTerrain = useLayerVisibilityStore((s) => s.layers.terrain);
  const altitudeSliderFt = useUiStore((s) => s.altitudeSliderFt);
  // 25-ft bucket: matches smallest red zone (heli-low preset uses 25 ft).
  const altitudeBucketFt = Math.round(altitudeSliderFt / 25) * 25;
  const taRedFt        = useTerrainAdvisorStore((s) => s.redFt);
  const taYellowFt     = useTerrainAdvisorStore((s) => s.yellowFt);
  const taActivationKt = useTerrainAdvisorStore((s) => s.activationSpeedKt);
  const taCorridorMode = useTerrainAdvisorStore((s) => s.corridorMode);

  const gpsSpeedMps = useGpsStore((s) => s.fix?.speedMps ?? null);
  const gsKt = gpsSpeedMps != null ? gpsSpeedMps * 1.94384 : null;
  // Activation gate: only suppress if we have a live speed reading below
  // the threshold. Without live speed (sim / planning), advisor stays on.
  const stationary = gsKt != null && gsKt < taActivationKt;

  // Memoize ramp inputs at 100-ft bucket resolution to avoid calling
  // setPaintProperty on every GPS fix (1 Hz). The dep array uses the
  // 25-ft altitudeBucketFt so sub-25 slider drags still reach the effect.
  const lastRampRef = useRef<{
    bucketFt: number;
    redFt: number;
    yellowFt: number;
    visible: boolean;
  } | null>(null);

  // rAF token — coalesce rapid GPS altitude changes into one paint.
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const apply = (): void => {
      const map = getMapInstance();
      if (!map) return;

      const tawsShouldRender =
        showTerrain
        && altitudeBucketFt <= CONUS_MAX_TERRAIN_FT
        && taCorridorMode !== 'hide'
        && !stationary;

      if (map.getLayer('terrain-hillshade')) {
        map.setLayoutProperty(
          'terrain-hillshade',
          'visibility',
          showTerrain ? 'visible' : 'none',
        );
      }
      if (map.getLayer('terrain-color')) {
        const prev = lastRampRef.current;
        const sameInputs =
          !!prev
          && prev.bucketFt === altitudeBucketFt
          && prev.redFt    === taRedFt
          && prev.yellowFt === taYellowFt
          && prev.visible  === tawsShouldRender;
        if (sameInputs) return;

        lastRampRef.current = {
          bucketFt: altitudeBucketFt,
          redFt: taRedFt,
          yellowFt: taYellowFt,
          visible: tawsShouldRender,
        };

        map.setLayoutProperty(
          'terrain-color',
          'visibility',
          tawsShouldRender ? 'visible' : 'none',
        );
        if (tawsShouldRender) {
          const yellowFloorM = (altitudeBucketFt - taYellowFt) / FT_PER_M;
          const redFloorM    = (altitudeBucketFt - taRedFt) / FT_PER_M;
          const eps = 0.001;
          const TRANSPARENT = 'rgba(0,0,0,0)';
          const YELLOW = 'rgba(250,184,26,1)';
          const RED    = 'rgba(217,25,25,1)';
          const ramp: unknown[] = [
            'interpolate', ['linear'], ['elevation'],
            -1000,              TRANSPARENT,
            yellowFloorM - eps, TRANSPARENT,
            yellowFloorM,       YELLOW,
            redFloorM - eps,    YELLOW,
            redFloorM,          RED,
            10000,              RED,
          ];
          map.setPaintProperty('terrain-color', 'color-relief-color', ramp as never);
        }
      }
    };

    const scheduledApply = (): void => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        apply();
      });
    };

    // Register with the style-rebuild registry so basemap switches
    // re-apply the ramp. The restore bypasses rAF (basemap rebuild
    // is a one-shot) and clears the memo so the "inputs unchanged"
    // guard doesn't short-circuit after the freshly-rebuilt layers
    // have the default empty ramp.
    const unregisterRebuild = registerStyleRebuild({
      id: 'terrain',
      restore: () => {
        lastRampRef.current = null;
        apply();
      },
    });

    scheduledApply();

    return () => {
      unregisterRebuild();
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    // Dep list: raw altitudeSliderFt so sub-25 ft drags reach the effect
    // (the bucket math and memo guard decide whether to do real work).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTerrain, altitudeSliderFt, taRedFt, taYellowFt, taCorridorMode, stationary]);

  return null;
}
